import React from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';
import { experimentColors } from '../../themes/experimentColors';
import { useTranslation } from 'react-i18next';

const ExperimentStatusIndicator = ({ 
  status, 
  startDate, 
  endDate, 
  width = 80 
}) => {
  const { t } = useTranslation();

  // Get color for status
  const statusColor = experimentColors[status] || experimentColors.pending;

  // Format date helper
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[date.getMonth()]} ${date.getDate()}`;
  };

  // Calculate progress for running experiments
  const calculateProgress = () => {
    if (status !== 'running') return 0;
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    const now = new Date();
    
    if (now < start) return 0;
    if (now > end) return 100;
    
    const totalDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    const elapsedDays = Math.ceil((now - start) / (1000 * 60 * 60 * 24));
    
    return Math.min(100, Math.max(0, (elapsedDays / totalDays) * 100));
  };


  const renderStatusContent = () => {
    switch (status) {
      case 'accepted':
        return (
          <Box sx={{ textAlign: 'center', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography sx={{ 
              fontSize: '9px', 
              fontWeight: 600,
              color: statusColor,
              lineHeight: 1.2,
              mb: 0.3,
              whiteSpace: 'nowrap'
            }}>
              {t('willStartOn')}
            </Typography>
            <Typography sx={{ 
              fontSize: '10px', 
              fontWeight: 500,
              color: statusColor,
              whiteSpace: 'nowrap'
            }}>
              {formatDate(startDate)}
            </Typography>
          </Box>
        );

      case 'running':
        const progress = calculateProgress();
        
        return (
          <Box sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                width: '100%',
                height: 8,
                borderRadius: 4,
                backgroundColor: 'rgba(16, 185, 129, 0.15)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: statusColor,
                  borderRadius: 4,
                }
              }}
            />
          </Box>
        );

      case 'finished':
        return (
          <Box sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
            <LinearProgress
              variant="determinate"
              value={100}
              sx={{
                width: '100%',
                height: 8,
                borderRadius: 4,
                backgroundColor: 'rgba(55, 65, 81, 0.15)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: statusColor,
                  borderRadius: 4,
                }
              }}
            />
          </Box>
        );

      default:
        return (
          <Typography sx={{ 
            fontSize: '10px', 
            fontWeight: 600,
            color: statusColor,
            textAlign: 'center'
          }}>
            {status}
          </Typography>
        );
    }
  };

  return (
    <Box 
      sx={{ 
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 30,
        px: 0.5
      }}
    >
      {renderStatusContent()}
    </Box>
  );
};

export default ExperimentStatusIndicator;